import { SessionContext, signOut } from "next-auth/react";
import { useContext } from "react";
import axios from "../api/axios";
import useErrorHandling from "./useErrorHanding";

const useApi = () => {
  const [handleError] = useErrorHandling();
  const sessions = useContext(SessionContext);
  const session = sessions?.data || { accessToken: "" };
  const get = async (endpoint) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${session.accessToken}` },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }
      error = handleError(err);
    }
    return { data, error };
  };

  const post = async (endpoint, postData, showError = true) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.post(endpoint, postData, {
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }

      error = handleError(err, showError);
    }

    return { data, error };
  };

  const put = async (endpoint, putData) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.put(endpoint, putData, {
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }
      error = handleError(err);
    }
    return { data, error };
  };

  const del = async (endpoint) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }
      error = handleError(err);
    }
    return { data, error };
  };

  const loadOptions = async (searchValue, loadFor) => {
    let data = null;
    let selectOptions = [];
    let error = null;
    let endpoint = null;
    let postData = {
      searchValue: searchValue,
    };
    switch (loadFor) {
      case "customer":
        endpoint = "api/search-customer";
        break;
      case "employee":
        endpoint = "api/search-employee";
        break;
      case "area":
        endpoint = "api/search-area";
        break;
      case "userGroup":
        endpoint = "api/search-user-group";
        break;
      case "user":
        endpoint = "api/search-user";
        break;
      case "department":
        endpoint = "api/search-department";
        break;
      case "skill":
        endpoint = "api/search-skill";
        break;
      case "schedule":
        endpoint = "api/search-schedule";
        break;
      case "item":
        endpoint = "api/search-item";
        break;
      case "group-items":
        endpoint = "api/search-group-item";
        break;
      case "key":
        endpoint = "api/search-key";
        break;
      case "tag":
        endpoint = "api/search-tag";
        break;
      case "category":
        endpoint = "api/search-category";
        break;
      case "project":
        endpoint = "api/search-project";
        break;
      case "project-id":
        endpoint = "api/search-project-id";
        break;
      default:
        endpoint = null;
        break;
    }
    try {
      const result = await axios.post(endpoint, postData, {
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      });
      data = result.data.results;
      selectOptions = data.map((option) => {
        return { value: option.id, label: option.label };
      });
    } catch (err) {
      if (err?.response.status === 401) {
        await signOut({ callbackUrl: "/login" });
      }
      error = handleError(err);
    }
    return { data, error, selectOptions };
  };

  return { get, post, put, del, loadOptions };
};

export default useApi;
